import { selectRelationToParent } from '@newStore/documentApi/documentApiSelectors';
import {
  selectContentNode,
  selectContentNodeChildren,
  selectIsNodeCollapsible,
  selectIsNodeProposedToDeleted,
  selectIsNodeSelected,
} from '@newStore/documentUI/documentUISelectors';
import { useDispatch, useSelector } from 'react-redux';

import InlineEditor from '@UI/editor/InlineEditor';
import { isFallbackNode, isLlinkidGoalSubItemNode } from '@newStore/documentUI/documentUITypes';
import { type MutableRefObject, useRef } from 'react';

import type { ContentHref } from '@generalTypes/apiTypes';
import Collapse from '@mui/material/Collapse';
import { getContentNodeId } from '@newStore/documentApi/documentApiHelpers';
import {
  editNode,
  selectAreSomeNodesCollapsed,
  toggleAllCollapsed,
  toggleCollapseNode,
} from '@newStore/documentUI/documentUIState';
import { selectAppliedNodeConfig } from '@newStore/documentUI/nodeTypeConfigSelectors';
import { selectValidationInfoForNode } from '@newStore/validation/validationSelectors';
import { NodeType } from '@nodeTypeConfig/configTypes';
import { toggleSelectionAction } from '@store/actions/documentActions';
import type { RootState } from '../../../../types/rootStateTypes';
import DragAndDropRow from '../DragnDrop/DragAndDrop';
import ContentChangedIndicator from '../changeIndicators/ContentChangedIndicator';
import ProposalIndicatorForDoc from '../changeIndicators/ProposalIndicatorForDoc';
import ValidationErrors from '../validationIndicator/ValidationErrors';
import ValidationIndicatorForDoc from '../validationIndicator/ValidationIndicatorForDoc';
import ContentReferencesLinks from './ContentReferencesLinks';
import './ContentRow.scss';
import { getPlaceHolderLabel } from './contentRowHelpers';
import {
  selectIsNodeDraggable,
  selectShouldDocumentShowCollapseAll,
  selectShowSelectBox,
} from './contentRowSelectors';
import AttachmentRow from './customContentRows/attachmentRow/AttachmentRow';
import AttachmentsGroupRow from './customContentRows/attachmentsGroupRow/AttachmentsGroupRow';
import GlobalDocumentRow from './customContentRows/globalDocumentRow/globalDocumentRow';
import ImageRow from './customContentRows/imageRow/ImageRow';
import KrcItemRow from './customContentRows/krcItemRow/KrcItemRow';
import LegalRow from './customContentRows/legalRow/legalRow';
import LlinkidCurriculumRow from './customContentRows/llinkidCurriculumRow/LlikindCurriculumRow';
import LlinkidGoalRow from './customContentRows/llinkidGoalRow/llinkidGoalRow';
import LlinkidGoalSubItemRow from './customContentRows/llinkidGoalSubItemRow/LlinkidGoalSubItemRow';
import MusicRow from './customContentRows/musicRow/musicRow';
import PersonReferenceRow from './customContentRows/personReferenceRow/PersonReferenceRow';
import ProNewsItemRow from './customContentRows/proNewsItemRow/ProNewsItemRow';
import QuoteRow from './customContentRows/quoteRow/QuoteRow';
import ReferenceGroupRow from './customContentRows/referenceGroupRow/ReferenceGroupRow';
import ReferenceRow from './customContentRows/referenceRow/ReferenceRow';
import TeaserRow from './customContentRows/teaserRow/TeaserRow';
import VideoRow from './customContentRows/videoRow/VideoRow';
import ZillLeerlijnReferenceStepRow from './customContentRows/zillLeerlijnRows/ZillLeerlijnReferenceStepRow';
import ZillLeerlijnStepRow from './customContentRows/zillLeerlijnRows/ZillLeerlijnStepRow';

const enableDragAndDrop = true;

const ContentRow: React.FC<{
  href: ContentHref;
  isRoot?: boolean;
  parentHref?: ContentHref;
}> = ({ href, isRoot, parentHref }) => {
  const dispatch = useDispatch();
  const contentNode = useSelector((state: RootState) => selectContentNode(state, href));
  const areSomeNodesCollapsed = useSelector(selectAreSomeNodesCollapsed);
  const collapsible = useSelector((state: RootState) => selectIsNodeCollapsible(state, href));
  const childHrefs = useSelector((state: RootState) => selectContentNodeChildren(state, href));
  const isCollapsed = useSelector((state: RootState) => state.documentUI.collapsedNodes[href]);
  const showCollapseAll = useSelector(selectShouldDocumentShowCollapseAll);

  const isDragging = useSelector((state: RootState) =>
    state.documentUI.draggingNodes.some(
      (node) => node.node.href === href && node.relation.to.href === parentHref,
    ),
  );

  const isProposedToDelete = useSelector((state: RootState) =>
    selectIsNodeProposedToDeleted(state, href),
  );

  const relationToParent = useSelector(
    (state: RootState) => parentHref && selectRelationToParent(state, href, parentHref),
  );
  const isSelected = useSelector(
    (state: RootState) => parentHref && selectIsNodeSelected(state, href, parentHref),
  );
  const showSelectionCheckbox = useSelector(
    (state: RootState) => parentHref && selectShowSelectBox(state, href, parentHref),
  );
  const isDraggable = useSelector(
    (state: RootState) => parentHref && selectIsNodeDraggable(state, href, parentHref),
  );

  const validation = useSelector((state: RootState) =>
    selectValidationInfoForNode(state, href, parentHref),
  );

  const nodeTypeConfig = useSelector((state: RootState) => selectAppliedNodeConfig(state, href));

  const rowRef = useRef() as MutableRefObject<HTMLDivElement>;
  const dragRef = useRef() as MutableRefObject<HTMLDivElement>;
  const dropChildZone = useRef() as MutableRefObject<HTMLDivElement>;

  if (!contentNode) {
    return null;
  }

  const editContentRow = () => {
    dispatch(editNode({ href: contentNode.href }));
  };

  const toggleSelectedRow = () => {
    if (relationToParent) {
      dispatch(toggleSelectionAction(relationToParent.key, true, true));
    } else {
      console.error('Relation not found');
    }
  };

  return (
    <DragAndDropRow
      contentNode={contentNode}
      parentHref={parentHref}
      rowRef={rowRef}
      dragRef={dragRef}
      dropChildZoneRef={dropChildZone}
      onUnCollapse={() => {
        if (isCollapsed) {
          dispatch(toggleCollapseNode({ nodeHref: href }));
        }
      }}
      disableDrag={!isDraggable}
      disableDrop={!enableDragAndDrop}
    >
      <div className="content-row-container">
        <div className="side-block">
          <div className="error-block">
            <ValidationErrors
              href={href}
              validationErrors={validation?.validationErrors}
              defaultMinimized={true}
            />
          </div>
        </div>
        <section
          ref={rowRef}
          className={`content-section${isDragging ? ' dragging' : ''} ${
            isSelected ? 'selected' : ''
          } ${isProposedToDelete ? 'deleted' : ''}
          `}
        >
          <ProposalIndicatorForDoc href={href} parentHref={parentHref || null} />

          <ContentChangedIndicator href={href} height90={true} />

          <div className="row-container">
            <div ref={dragRef} className={`drag-handle ${!isDraggable && 'disabled'}`}>
              <img src="images/drag-handle.svg" draggable="false" />
            </div>

            {!isProposedToDelete && (
              <ValidationIndicatorForDoc href={href} parentHref={parentHref} />
            )}

            {isRoot && !!childHrefs?.length && showCollapseAll && (
              <div
                className="expand-collapse-icon all"
                onClick={() => dispatch(toggleAllCollapsed())}
              >
                {areSomeNodesCollapsed ? (
                  <img
                    src="images/expand-all-arrow-icon.svg"
                    draggable="false"
                    title="Klap alles open"
                  />
                ) : (
                  <img
                    src="images/collapse-all-arrow-icon.svg"
                    draggable="false"
                    title="Klap alles dicht"
                  />
                )}
              </div>
            )}
            {collapsible && !!childHrefs?.length && (
              <div
                className={`expand-collapse-icon ${isCollapsed ? 'collapsed' : 'expanded'}`}
                onClick={() => dispatch(toggleCollapseNode({ nodeHref: href }))}
              >
                {isCollapsed && <img src="images/expand-arrow-icon.svg" draggable="false" />}
                {!isCollapsed && <img src="images/collapse-arrow-icon.svg" draggable="false" />}
              </div>
            )}

            <div
              className={`inner-content-row type-${contentNode.type.toLowerCase()}`}
              id={getContentNodeId({ childHref: href, parentHref })}
            >
              <ContentReferencesLinks contentNodeHref={contentNode.href} />

              {'icon' in contentNode && contentNode.icon && (
                <span
                  className="structure-icon-wrapper"
                  style={{ backgroundColor: contentNode.color }}
                >
                  <img
                    src={contentNode.icon.url}
                    style={{ height: '25px', width: '25px' }}
                    draggable="false"
                  />
                </span>
              )}
              {(!('customRender' in contentNode) || !contentNode.customRender) && (
                <div>
                  <div className="node-title">
                    {'identifier' in contentNode && (
                      <div
                        style={{
                          verticalAlign: 'top',
                          whiteSpace: 'nowrap',
                          color:
                            'color' in contentNode && contentNode.color
                              ? contentNode.color
                              : 'black',
                        }}
                        className="identifier"
                      >
                        <div id={href} contentEditable={false}>
                          {contentNode.identifier}
                        </div>
                      </div>
                    )}
                    {'title' in contentNode && (
                      <InlineEditor
                        initial={contentNode.title}
                        field={('field' in contentNode && contentNode.field) || 'title'}
                        placeholder={getPlaceHolderLabel(nodeTypeConfig, 'title')}
                        skey={contentNode.key}
                        href={contentNode.href}
                      />
                    )}
                  </div>

                  {('text' in contentNode || 'description' in contentNode) && (
                    <div className="node-text">
                      {'image' in contentNode && contentNode.image && (
                        <img
                          className="node-illustration"
                          src={contentNode.image.url}
                          style={{ width: '150px' }}
                          draggable="false"
                        />
                      )}
                      <div>
                        {'description' in contentNode && (
                          <InlineEditor
                            initial={contentNode.description}
                            field="description"
                            placeholder={getPlaceHolderLabel(nodeTypeConfig, 'description')}
                            skey={contentNode.key}
                            href={contentNode.href}
                          />
                        )}
                        {'text' in contentNode && 'description' in contentNode && <br />}
                        {'text' in contentNode && (
                          <div>
                            {'isStaticHtml' in contentNode && contentNode.isStaticHtml ? (
                              <pre>
                                <code style={{ display: 'block' }}>{contentNode.text}</code>
                              </pre>
                            ) : (
                              <InlineEditor
                                initial={contentNode.text}
                                field="html"
                                placeholder={getPlaceHolderLabel(nodeTypeConfig, 'html')}
                                skey={contentNode.key}
                                href={contentNode.href}
                              />
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {isFallbackNode(contentNode) && (
                    <div className="warning-label">
                      {contentNode.type} has no viewmodel selector!
                    </div>
                  )}

                  {'placeholder' in contentNode && (
                    <div className="node-title" style={{ color: '#a0a0a0', cursor: 'default' }}>
                      [{contentNode.placeholder}]
                    </div>
                  )}
                </div>
              )}

              {'customRender' in contentNode && contentNode.customRender && (
                <>
                  {contentNode.type === NodeType.ATTACHMENTS_GROUP && (
                    <AttachmentsGroupRow contentNode={contentNode} />
                  )}
                  {contentNode.type === NodeType.TEASER && <TeaserRow contentNode={contentNode} />}
                  {contentNode.type === NodeType.VIDEO && <VideoRow contentNode={contentNode} />}
                  {contentNode.type === NodeType.CURRICULUM_ZILL_LEERLIJN_CLUSTER && (
                    <ZillLeerlijnStepRow contentNode={contentNode} />
                  )}
                  {(contentNode.type === NodeType.CURRICULUM_ZILL_LEERLIJN_POST_REFERENCE ||
                    contentNode.type === NodeType.CURRICULUM_ZILL_LEERLIJN_PRE_REFERENCE) && (
                    <ZillLeerlijnReferenceStepRow contentNode={contentNode} />
                  )}
                  {contentNode.type === NodeType.QUOTE && <QuoteRow contentNode={contentNode} />}
                  {isLlinkidGoalSubItemNode(contentNode) && (
                    <LlinkidGoalSubItemRow contentNode={contentNode} />
                  )}
                  {contentNode.type === NodeType.ATTACHMENT && (
                    <AttachmentRow contentNode={contentNode} />
                  )}
                  {contentNode.type === NodeType.IMAGE && <ImageRow contentNode={contentNode} />}
                  {contentNode.type === NodeType.GLOBAL_DOCUMENT && (
                    <GlobalDocumentRow contentNode={contentNode} />
                  )}
                  {contentNode.type === NodeType.LEGAL && <LegalRow contentNode={contentNode} />}
                  {contentNode.type === NodeType.REFERENCE_GROUP && (
                    <ReferenceGroupRow contentNode={contentNode} />
                  )}
                  {(contentNode.type === NodeType.REFERENCE ||
                    contentNode.type === NodeType.PRO_NEWSLETTER_EVENT) && (
                    <ReferenceRow contentNode={contentNode} />
                  )}
                  {(contentNode.type === NodeType.KRC_GOAL_SET_ITEM ||
                    contentNode.type === NodeType.KRC_AGE_RANGE_ITEM) && (
                    <KrcItemRow contentNode={contentNode} />
                  )}
                  {contentNode.type === NodeType.PERSON_REFERENCE && (
                    <PersonReferenceRow contentNode={contentNode} />
                  )}
                  {contentNode.type === NodeType.MUSIC && <MusicRow contentNode={contentNode} />}
                  {contentNode.type === NodeType.LLINKID_GOAL && (
                    <LlinkidGoalRow contentNode={contentNode} />
                  )}
                  {contentNode.type === NodeType.PRONEWSITEM && (
                    <ProNewsItemRow contentNode={contentNode} />
                  )}
                  {contentNode.type === NodeType.LLINKID_CURRICULUM && (
                    <LlinkidCurriculumRow contentNode={contentNode} />
                  )}
                  {contentNode.type === NodeType.LLINKID_CONCRETIZATION && (
                    <LlinkidGoalSubItemRow contentNode={contentNode} />
                  )}
                </>
              )}
            </div>

            <div className="type-label">{nodeTypeConfig.information.single}</div>

            {(!('readOnlyNode' in nodeTypeConfig) || !nodeTypeConfig.readOnlyNode) && (
              <div className="edit-button">
                <span className="glyphicon glyphicon-edit" onClick={() => editContentRow()} />
              </div>
            )}
            {showSelectionCheckbox && (
              <div className="select-action">
                <input type="checkbox" onChange={toggleSelectedRow} checked={isSelected} />
                <span className="customCheckmark" onClick={toggleSelectedRow} />
              </div>
            )}
          </div>
        </section>
        <div className="content-row-children">
          <div className="dropChild" ref={dropChildZone} />
          {!isDragging && childHrefs && childHrefs.length > 0 && (
            <Collapse in={!isCollapsed} timeout={{ appear: 0, enter: 0, exit: 400 }} unmountOnExit>
              {childHrefs?.map((childHref) => (
                <ContentRow key={childHref} href={childHref} parentHref={contentNode.href} />
              ))}
            </Collapse>
          )}
        </div>
      </div>
    </DragAndDropRow>
  );
};

export default ContentRow;
